// import data from "../data/data.json";
import { cacheBustUrl } from './utils';

import { appRouter } from './appRouter'

import { injectLineStringIntoFeatureCollection } from './geoUtils';
import { HalteParentType, isValidHalteParentType } from './HalteParentType';

class Db
{

    constructor(){
        this.data = null;
        this.__parcoursSortedArray = null;

        this.parcoursPathsMap = new Map(); // map of parcours slugs to geojson paths

        this.stepsContentCache = new Map();

        this.loaded = false;
    }

    async load(){
        const loadData = async () => {
            const dataURL = cacheBustUrl('/data/data.json');
            const response = await fetch(appRouter.normalizePath(dataURL));
            return await response.json();
        }
        this.data = await loadData();
    }

    // get geojson path
    async getParcoursPath(parcoursSlug){
        if(this.parcoursPathsMap.get(parcoursSlug)){
            return this.parcoursPathsMap.get(parcoursSlug);
        }
        const parcours = this.getParcoursWithSlug(parcoursSlug);
        const url = appRouter.normalizePath(parcours.media.path);

        // console.log("fetching path", url);

        const response = await fetch(url);
        const string = await response.text();
        const data = JSON.parse(injectLineStringIntoFeatureCollection(string))
        this.parcoursPathsMap.set(parcoursSlug, data);
        return data;
    }

    async loadResource(url){
        url = cacheBustUrl(url);
        const fullUrl = appRouter.normalizePath(url);
        console.log("loading url", fullUrl);
        const response = await fetch(fullUrl);
        const data = await response.json();
        return data;
    }

    async getHalteContentFor(parentSlug, halteSlug, parentType){
        console.log("halte content for", parentSlug, halteSlug, parentType);
        if(!parentType){
            throw new Error("parent type must be set");
        }

        let step = null;
        if(parentType === HalteParentType.parcours){
            step = this.getParcoursStep(parentSlug, halteSlug);
        }
        
        if(parentType === HalteParentType.constellation){
            step = this.getConstellationStep(parentSlug, halteSlug);
        }

        if(!step) return "";

        const key = step.contentMap.main_url;

        if(!this.stepsContentCache.has(key)){
            const data = await this.loadResource(key);
            if(data.cards){
                const trackStep = {
                    html: data.html,
                    cards: data.cards,
                }
                this.stepsContentCache.set(key, trackStep);
            }else if (data.html){
                this.stepsContentCache.set(key, data.html);
            }else{
                throw new Error(`invalid step found for slug ${halteSlug} and content ${data}`);
            }
        }

        return this.stepsContentCache.get(key);
    }

    getAllHaltes(){
        let steps = []
        for(const parcours of this.data.parcours){
            for(const step of parcours.steps){
                steps.push(step);
            }
        }

        return steps;
    }

    getAllHaltesOfType(type){
        return this.getAllHaltes().filter(step => step.type === type);
    }
    
    async getBonusContentFor(parcoursSlug, halteSlug){
        const step = this.getParcoursStep(parcoursSlug, halteSlug);
        if(!step) return "";

        const key = step.contentMap.bonus_url;

        if(!this.stepsContentCache.has(key)){
            const data = await this.loadResource(key);
            this.stepsContentCache.set(key, data.html);
        }

        return this.stepsContentCache.get(key);
    }
    
    async getBiblioContentFor(parcoursSlug, halteSlug){
        const step = this.getParcoursStep(parcoursSlug, halteSlug);
        if(!step) return "";

        const key = step.contentMap.bibliography_url;

        if(!this.stepsContentCache.has(key)){
            const data = await this.loadResource(key);
            this.stepsContentCache.set(key, data.html);
        }

        return this.stepsContentCache.get(key);
    }

    getIndex(){
        return this.data.index;
    }

    isValidParcoursSlug(slug){
        return !!this.data.parcours.find(p => p.slug === slug); 
    }
    
    isValidConstellationSlug(slug){
        return !!this.data.constellations.find(p => p.slug === slug); 
    }

    getStepForParentType(parentSlug, halteSlug, parentType){
        if(!isValidHalteParentType(parentType)){
            console.error("invalid parent type", parentType);
            return null;
        }
        
        if(parentType === HalteParentType.parcours){
            return this.getParcoursStep(parentSlug, halteSlug);
        }

        if(parentType === HalteParentType.constellation){
            return this.getConstellationStep(parentSlug, halteSlug);
        }
    }

    getParcoursStep(parentSlug, halteSlug){
        console.log("getParcoursStep called with", parentSlug, halteSlug);
        const parcours = this.getParcoursWithSlug(parentSlug);

        if(!parcours) {
            throw 32;
            return null;
        }
        return parcours.steps.find(s => s.slug == halteSlug);
    }
    
    getConstellationStep(parentSlug, halteSlug){
        const constellation = this.getConstellationWithSlug(parentSlug);
        console.log("getConstellationStep called with", parentSlug, halteSlug);
        console.log("got", constellation);

        if(!constellation){
            return null;
        }
        return constellation.steps.find(s => s.slug == halteSlug);
    }

    // can only get a next step in a parcours context
    getNextStep(step){
        if(!step) return null;

        const parentParcours = this.getParcoursWithSlug(step.parentSlug);
        if(!parentParcours){
            return null;
        }

        const index = parentParcours.steps.indexOf(step);

        if(index + 1 < parentParcours.steps.length){
            return parentParcours.steps[index + 1];
        }else{
            return null;
        }
    }

    isValidHalteParamsForParentType(parentSlug, halteSlug, parentType){
        if(parentType === HalteParentType.parcours){
            return !!this.getParcoursStep(parentSlug, halteSlug);
        }
        
        if(parentType === HalteParentType.constellation){
            return !!this.getConstellationStep(parentSlug, halteSlug);
        }

        console.error("invalid halteParentType found", parentType);
        return false;
    }

    getParcoursWithSlug(slug){
        return this.data.parcours.find(p => p.slug === slug);
    }

    getConstellationWithSlug(slug){
        return this.data.constellations.find(p => p.slug === slug);
    }

    getAllParcours(){
        return this.data.parcours;
    }

    getStepsForParcours(parcoursSlug){
        return this.getParcoursWithSlug(parcoursSlug).steps;
    }

    // handle route redirection from qr code such as /qr/:a/:b
    redirectForParcoursIdAndHalteId(parcoursId, halteId){
        parcoursId = +parcoursId;
        halteId = +halteId;

        if(isNaN(parcoursId) || isNaN(halteId)){
            return null;
        }

        if(parcoursId >= this.data.parcours.length){
            return null;
        }

        const parcours = this.data.parcours[parcoursId];
        const parcoursSlug = parcours.slug; 
        if(halteId >= parcours.steps.length){
            return null;
        }

        const halteSlug = parcours.steps[halteId].slug;

        return `/parcours/${parcoursSlug}/halte/${halteSlug}`;
    }

    get aboutContent(){
        return this.data.about || "";
    }

    get eventsContent(){
        return this.data.events || "";
    }

    get accueilParcoursContent(){
        return this.data.accueilParcours;
    }

    getHalteCoordsForParcours(parcoursSlug){
        const parcours = this.getParcoursWithSlug(parcoursSlug);
        if(!parcours){
            return null;
        }

        return parcours.steps.map(s => s.coordinates);
    }
    
    getHalteCoordsForConstellationWithSlug(constellationSlug){
        const constellation = this.getConstellationWithSlug(constellationSlug);
        if(!constellation){
            return [];
        }

        return constellation.steps.map(s => s.coordinates);
    }

    getHalteSlugForParcoursSludAndStepIndex(parcoursSlug, halteIndex){
        return this.getParcoursWithSlug(parcoursSlug).steps[halteIndex].slug;
    }
}

export const db = new Db(); 
